import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    Output,
} from "@angular/core"
import { ISongQueueEntry } from "@common-lib/classes/songQueue"

@Component({
    selector: "song-lyrics-panel",
    templateUrl: "song-lyrics-panel.component.html",
    styleUrls: ["song-lyrics-panel.component.scss"],
})
export class SongLyricsPanelComponent {
    @Input() public song: ISongQueueEntry | null = null
    @Input() public width: number = 400

    @Output() public close = new EventEmitter<void>()
    @Output() public widthChange = new EventEmitter<number>()

    public readonly minWidth: number = 320
    public readonly maxWidth: number = 700
    public resizing: boolean = false

    private resizeStartX: number = 0
    private resizeStartWidth: number = 400

    public get hasLyrics(): boolean {
        return (
            typeof this.song?.lyrics === "string" &&
            this.song.lyrics.trim().length > 0
        )
    }

    public closePanel(): void {
        this.resizing = false
        this.close.emit()
    }

    public onResizePointerDown(event: PointerEvent): void {
        const handle = event.currentTarget as HTMLElement

        event.preventDefault()
        this.resizing = true
        this.resizeStartX = event.clientX
        this.resizeStartWidth = this.width
        handle.setPointerCapture?.(event.pointerId)
    }

    public onResizePointerMove(event: PointerEvent): void {
        if (!this.resizing) {
            return
        }

        const delta = this.resizeStartX - event.clientX
        this.setWidth(this.resizeStartWidth + delta)
    }

    public onResizePointerUp(event: PointerEvent): void {
        if (!this.resizing) {
            return
        }

        this.onResizePointerMove(event)
        this.resizing = false

        const handle = event.currentTarget as HTMLElement
        handle.releasePointerCapture?.(event.pointerId)
    }

    public onResizePointerCancel(): void {
        this.resizing = false
    }

    public onResizeKeydown(event: KeyboardEvent): void {
        let nextWidth = this.width

        switch (event.key) {
            case "ArrowLeft":
                nextWidth += 20
                break
            case "ArrowRight":
                nextWidth -= 20
                break
            case "Home":
                nextWidth = this.minWidth
                break
            case "End":
                nextWidth = this.maxWidth
                break
            default:
                return
        }

        event.preventDefault()
        this.setWidth(nextWidth)
    }

    @HostListener("document:keydown.escape")
    public onEscape(): void {
        this.closePanel()
    }

    private setWidth(width: number): void {
        const nextWidth = this.clampWidth(width)

        if (nextWidth === this.width) {
            return
        }

        this.width = nextWidth
        this.widthChange.emit(nextWidth)
    }

    private clampWidth(width: number): number {
        const rootStyles = getComputedStyle(document.documentElement)
        const sidebarWidth =
            parseFloat(rootStyles.getPropertyValue("--sidebar-width")) || 0
        const availableWidth = window.innerWidth - sidebarWidth - 420 - 16
        const maximumWidth = Math.max(
            this.minWidth,
            Math.min(this.maxWidth, availableWidth)
        )

        return Math.round(
            Math.min(maximumWidth, Math.max(this.minWidth, width))
        )
    }
}
